import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';

interface ListOption {
  id: number;
  value: string;
  sort_order: number;
}

type GroupedLists = Record<string, ListOption[]>;

const TAB_CATEGORIES = [
  { key: 'file_status', label: 'File Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'case_type', label: 'Case Type' },
  { key: 'lead_type', label: 'Lead Type' },
  { key: 'payment_method', label: 'Payment Method' },
  { key: 'case_stages', label: 'Case Stages' },
];

export default function SettingsPage() {
  const [lists, setLists] = useState<GroupedLists>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(TAB_CATEGORIES[0].key);
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);

  function fetchLists() {
    apiFetch('/api/lists').then(r => r.json()).then((data: GroupedLists) => { setLists(data); setLoading(false); }).catch(() => setLoading(false));
  }

  useEffect(() => { fetchLists(); }, []);

  async function handleAdd() {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await apiFetch('/api/lists', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ category: activeTab, value: trimmed }) });
      setNewValue('');
      fetchLists();
    } finally { setSaving(false); }
  }

  async function handleDelete(id: number) {
    await apiFetch(`/api/lists/${id}`, { method: 'DELETE' });
    fetchLists();
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  const currentItems = lists[activeTab] || [];
  const currentTabLabel = TAB_CATEGORIES.find(t => t.key === activeTab)?.label || activeTab;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Configure system options and dropdown lists</p>
        </div>
      </div>

      <div className="tabs">
        {TAB_CATEGORIES.map((tab) => (
          <button key={tab.key} className={`tab${activeTab === tab.key ? ' active' : ''}`} onClick={() => { setActiveTab(tab.key); setNewValue(''); }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">{currentTabLabel}</h2>
          <span className="badge badge-gray">{currentItems.length} item{currentItems.length !== 1 ? 's' : ''}</span>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <input className="form-input" type="text" placeholder={`Add new ${currentTabLabel.toLowerCase()} value...`} value={newValue} onChange={(e) => setNewValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAdd()} style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={handleAdd} disabled={saving || !newValue.trim()}>{saving ? 'Adding...' : 'Add'}</button>
        </div>

        {currentItems.length === 0 ? (
          <div className="empty-state"><p>No values configured for {currentTabLabel}. Add one above.</p></div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {currentItems.map((item) => (
              <span key={item.id} className="badge badge-blue" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px' }}>
                {item.value}
                <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700, fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2, opacity: 0.7 }} title={`Remove "${item.value}"`}>&times;</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
